/* Admin Quick Pack builder.
 *
 * A Quick Pack is a saved playlist of document templates that any dealer
 * can launch from a customer profile to produce ONE merged + autofilled PDF.
 *
 * Permissions: admin / owner / super_admin only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

const VIS_ROLES = [
  "owner", "admin", "general_manager", "sales_manager",
  "salesperson", "fni", "bdc", "service",
];

export default function AdminQuickPacks() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);  // null | "new" | <pack obj>
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (ready && (!isDealerUser(user) || !["owner", "admin", "super_admin"].includes(user.role))) {
      navigate("/dealer-login");
    }
  }, [ready, user, navigate]);

  const load = async () => {
    const [p, t] = await Promise.all([
      api.get("/admin/documents/quick-packs"),
      api.get("/admin/documents/templates", { params: { active_only: true } }),
    ]);
    setPacks(p.data.items || []);
    setTemplates(t.data.items || []);
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const startNew = () => setEditing({
    id: null, name: "", description: "", folder: "",
    role_visibility: null, items: [], active: true, sort_order: 0,
  });

  const remove = async (pk) => {
    if (!window.confirm(`Delete Quick Pack "${pk.name}"?`)) return;
    await api.delete(`/admin/documents/quick-packs/${pk.id}`);
    setMsg(`Deleted "${pk.name}".`);
    await load();
  };

  const toggleActive = async (pk) => {
    setBusy(true);
    try {
      await api.put(`/admin/documents/quick-packs/${pk.id}`, {
        name: pk.name, description: pk.description || "",
        folder: pk.folder || null, role_visibility: pk.role_visibility,
        items: (pk.items || []).map(i => ({ template_id: i.template_id, label: i.label, required: i.required })),
        active: !pk.active, sort_order: pk.sort_order || 0,
      });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="ssc-container py-6" data-testid="admin-quick-packs">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <Link to="/admin/crm" className="text-xs text-blue-600 hover:underline">← CRM</Link>
          <h1 className="text-2xl font-bold text-[#061426] mt-1">Quick Pack Builder</h1>
          <p className="text-sm text-gray-500">
            Bundle multiple Deal Jacket forms into a single one-click PDF packet. Salespeople tap one button on the customer profile; the system autofills every template and merges them into one printable PDF saved to the Deal Jacket.
          </p>
        </div>
        <button onClick={startNew} className="ssc-btn-primary text-sm" data-testid="qp-new">+ New Quick Pack</button>
      </div>

      {msg && <div className="text-xs text-emerald-700 mb-2" data-testid="qp-msg">{msg}</div>}

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="qp-table">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Forms</th><th className="text-left p-2">Visible to</th><th className="text-left p-2">Active</th><th></th></tr>
          </thead>
          <tbody>
            {packs.map(pk => (
              <tr key={pk.id} className="border-t hover:bg-blue-50/40" data-testid={`qp-row-${pk.id}`}>
                <td className="p-2"><div className="font-bold">{pk.name}</div><div className="text-xs text-gray-500">{pk.description}</div></td>
                <td className="p-2 text-xs">
                  <ol className="list-decimal list-inside space-y-0.5">
                    {(pk.items || []).slice(0, 6).map(it => (
                      <li key={it.template_id} className={it.template_active ? "" : "text-red-600"}>
                        {it.label || it.template_name}{!it.template_active && " · MISSING"}
                      </li>
                    ))}
                    {(pk.items || []).length > 6 && <li>+ {(pk.items || []).length - 6} more</li>}
                  </ol>
                </td>
                <td className="p-2 text-xs">{pk.role_visibility ? pk.role_visibility.join(", ") : "(everyone)"}</td>
                <td className="p-2">
                  <button onClick={() => toggleActive(pk)} disabled={busy}
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${pk.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"}`}
                    data-testid={`qp-toggle-${pk.id}`}>
                    {pk.active ? "Active" : "Off"}
                  </button>
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(pk)} className="text-xs text-blue-600 hover:underline mr-2" data-testid={`qp-edit-${pk.id}`}>Edit</button>
                  <button onClick={() => remove(pk)} className="text-xs text-red-600 hover:underline" data-testid={`qp-del-${pk.id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {packs.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gray-400">No Quick Packs yet. Click <strong>+ New Quick Pack</strong> to bundle your most-used forms.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <QuickPackEditor
          pack={editing}
          templates={templates}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); setMsg("Saved."); await load(); }}
        />
      )}
    </div>
  );
}

function QuickPackEditor({ pack, templates, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => ({
    ...pack,
    items: (pack.items || []).map(it => ({ template_id: it.template_id, label: it.label || "", required: it.required !== false })),
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const folders = useMemo(() => Array.from(new Set(templates.map(t => t.folder).filter(Boolean))).sort(), [templates]);
  const [folderFilter, setFolderFilter] = useState("");
  const [q, setQ] = useState("");

  const visibleTemplates = useMemo(() => {
    let out = templates;
    if (folderFilter) out = out.filter(t => t.folder === folderFilter);
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter(t => (t.name || "").toLowerCase().includes(needle) || (t.category || "").toLowerCase().includes(needle));
    }
    return out;
  }, [templates, folderFilter, q]);

  const addItem = (t) => {
    if (draft.items.some(it => it.template_id === t.id)) return;
    setDraft(d => ({ ...d, items: [...d.items, { template_id: t.id, label: "", required: true }] }));
  };
  const removeItem = (i) => setDraft(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }));
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draft.items.length) return;
    const next = [...draft.items];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft(d => ({ ...d, items: next }));
  };
  const setLabel = (i, label) => setDraft(d => ({ ...d, items: d.items.map((it, idx) => idx === i ? { ...it, label } : it) }));

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const body = {
        name: draft.name.trim(),
        description: draft.description || "",
        folder: draft.folder || null,
        role_visibility: draft.role_visibility && draft.role_visibility.length ? draft.role_visibility : null,
        items: draft.items.map(it => ({ template_id: it.template_id, label: it.label || null, required: it.required !== false })),
        active: !!draft.active,
        sort_order: parseInt(draft.sort_order || 0, 10) || 0,
      };
      if (!body.name) { setErr("Name is required."); setBusy(false); return; }
      if (!body.items.length) { setErr("Add at least one form."); setBusy(false); return; }
      if (draft.id) {
        await api.put(`/admin/documents/quick-packs/${draft.id}`, body);
      } else {
        await api.post("/admin/documents/quick-packs", body);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const toggleRole = (r) => setDraft(d => {
    const cur = d.role_visibility || [];
    const next = cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r];
    return { ...d, role_visibility: next };
  });
  const tplName = (id) => (templates.find(t => t.id === id) || {}).name || "(missing)";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="qp-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
          <h2 className="font-bold">{draft.id ? "Edit Quick Pack" : "New Quick Pack"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="qp-editor-close">×</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-5 p-5 overflow-y-auto">
          {/* Left: metadata + ordered items */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Name</label>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="w-full border rounded px-2 py-1.5" data-testid="qp-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Description</label>
              <textarea value={draft.description || ""} rows={2}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="qp-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Folder (optional)</label>
                <input value={draft.folder || ""} onChange={(e) => setDraft({ ...draft, folder: e.target.value })}
                  className="w-full border rounded px-2 py-1" data-testid="qp-folder" placeholder="e.g. Sales · F&I · Service" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Sort order</label>
                <input type="number" value={draft.sort_order || 0}
                  onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                  className="w-full border rounded px-2 py-1" data-testid="qp-sort" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500">Visible to roles <span className="text-gray-400">(leave blank = everyone)</span></label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="qp-roles">
                {VIS_ROLES.map(r => {
                  const on = (draft.role_visibility || []).includes(r);
                  return (
                    <button key={r} type="button" onClick={() => toggleRole(r)}
                      className={`text-xs px-2 py-0.5 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700"}`}
                      data-testid={`qp-role-${r}`}>{r}</button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                  data-testid="qp-active" />
                <span>Active (visible to salespeople)</span>
              </label>
            </div>

            {/* Ordered item list */}
            <div className="border rounded">
              <div className="px-3 py-2 bg-gray-50 border-b text-xs font-bold uppercase">Forms in this pack ({draft.items.length})</div>
              {draft.items.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">Add forms from the right panel →</div>}
              <ol className="divide-y" data-testid="qp-items">
                {draft.items.map((it, i) => (
                  <li key={it.template_id} className="px-3 py-2 flex items-center gap-2" data-testid={`qp-item-${it.template_id}`}>
                    <span className="text-gray-400 w-5 text-right">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{tplName(it.template_id)}</div>
                      <input value={it.label} placeholder="Custom label (optional)"
                        onChange={(e) => setLabel(i, e.target.value)}
                        className="w-full text-xs border-b border-gray-200 focus:border-blue-500 outline-none"
                        data-testid={`qp-item-label-${it.template_id}`} />
                    </div>
                    <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="text-xs px-1 disabled:opacity-30" data-testid={`qp-item-up-${it.template_id}`}>↑</button>
                    <button onClick={() => moveItem(i, +1)} disabled={i === draft.items.length - 1} className="text-xs px-1 disabled:opacity-30" data-testid={`qp-item-down-${it.template_id}`}>↓</button>
                    <button onClick={() => removeItem(i)} className="text-xs text-red-600 px-1" data-testid={`qp-item-rm-${it.template_id}`}>×</button>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right: template browser */}
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase text-gray-500">Add forms</div>
            <div className="flex gap-2">
              <select value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="qp-folder-filter">
                <option value="">All folders</option>
                {folders.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
                className="text-xs border rounded px-2 py-1 flex-1" data-testid="qp-tpl-search" />
            </div>
            <div className="border rounded max-h-[55vh] overflow-y-auto" data-testid="qp-tpl-list">
              {visibleTemplates.map(t => {
                const used = draft.items.some(it => it.template_id === t.id);
                return (
                  <div key={t.id} className={`px-3 py-2 border-b flex items-center justify-between gap-2 ${used ? "bg-emerald-50" : "hover:bg-blue-50/40"}`}
                    data-testid={`qp-tpl-${t.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate">{t.name}</div>
                      <div className="text-[10px] text-gray-500">{t.folder} · {t.category} · v{t.version} · {t.file_kind === "pdf" ? (t.has_acroform ? "AcroForm PDF" : "Overlay PDF") : "Image"}</div>
                    </div>
                    <button onClick={() => addItem(t)} disabled={used}
                      className={`text-xs px-2 py-1 rounded ${used ? "bg-emerald-200 text-emerald-700" : "bg-blue-600 text-white"} disabled:cursor-default`}
                      data-testid={`qp-add-${t.id}`}>{used ? "Added" : "+ Add"}</button>
                  </div>
                );
              })}
              {visibleTemplates.length === 0 && <div className="p-6 text-center text-gray-400 text-xs">No templates match.</div>}
            </div>
            <div className="text-[10px] text-gray-500">Tip: order matters — the merged PDF appears in the order shown on the left.</div>
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
          {err ? <div className="text-xs text-red-600" data-testid="qp-editor-err">{err}</div> : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-3 py-1.5 border rounded">Cancel</button>
            <button onClick={save} disabled={busy} className="ssc-btn-primary text-sm" data-testid="qp-editor-save">
              {busy ? "Saving…" : "Save Quick Pack"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
